import { getToken } from "../../users/service/localStorage";
// Runtime, not build-time: roaming is the server's decision, and a value
// compiled into the APK cannot be turned back off without a Play release.
import { isRoamingEnabled } from "../../utils/runtimeFlags";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

/**
 * First-trade mission — derived server-side from what the account contains.
 *
 * Deliberately NOT stored client-side. The previous onboarding was gated by a
 * localStorage flag, which meant it replayed on every new device and claimed
 * completion whether or not the user had done anything. Asking the server what
 * the account actually holds removes both problems and leaves no progress
 * record to drift out of sync.
 *
 * Uses fetch rather than axios, matching analytics.js and lib/errorReporter:
 * axios is ESM in the CRA Jest bundle, and this module is imported by the map,
 * so pulling axios in here would drag it into those test bundles too. It also
 * keeps this out of the global axios error interceptor — a failed mission fetch
 * must never raise a snackbar at the user.
 */
// MapPage and OnboardingCoach both mount around the same time on a fresh
// /map load and each ask for the mission independently. Sharing one in-flight
// request means two callers in the same window get one network call instead
// of two; a caller after the previous request settled still gets a fresh one.
let inFlightMissionRequest = null;

export const fetchFirstTradeMission = async () => {
  if (inFlightMissionRequest) return inFlightMissionRequest;

  const token = getToken();
  inFlightMissionRequest = (async () => {
    const res = await fetch(`${apiUrl}/users/me/mission`, {
      headers: token ? { "x-auth-token": token } : {},
    });
    if (!res.ok) throw new Error(`mission fetch failed: ${res.status}`);
    return res.json();
  })();

  try {
    return await inFlightMissionRequest;
  } finally {
    inFlightMissionRequest = null;
  }
};

/** Copy per step. Kept beside the ids so a renamed step fails loudly. */
export const STEP_COPY = {
  add_cards: {
    title: "Add a few cards",
    // Honest about the Vault (P0 area B): "+" files a card in the Vault, which
    // shows on your profile but never matches. The old line — "the app matches
    // on your real collection" — was false for every card added that way.
    body: "Start with what you already own. New cards go to your Vault; you choose which ones to put up for trade.",
    cta: "Browse cards",
    href: "/pokemontcg",
  },
  build_wishlist: {
    title: "Say what you're hunting",
    body: "Your wishlist is what turns nearby collectors into matches. Without it the map has nothing to look for.",
    cta: "Build wishlist",
    href: "/wishlist",
  },
  // The server's `matchable` step now means "at least one card in your Trade
  // Binder (or For Sale) with a photo of it" — exactly what other collectors'
  // matching uses (P0 area B). The copy says so in plain words.
  matchable: {
    title: "Make a card findable",
    body: "Put a card in your Trade Binder and add a photo of it. That's what lets nearby collectors match with you — Vault cards show on your profile but can't be traded.",
    cta: "List a card for trade",
    href: "/collection",
  },
  propose_trade: {
    title: "Propose your first trade",
    body: "Tap a match on the map, reveal what they have, and build a trade.",
    cta: "Open map",
    href: "/map",
  },
  complete_trade: {
    title: "Meet and complete it",
    body: "Meet in public, inspect the cards, then both confirm the swap so it counts.",
    cta: "Open trades",
    href: "/trades",
  },
};

/**
 * The `matchable` step when the cards are fine but the OWNER cannot be found
 * (skeptic, launch sprint): discovery skips Hidden collectors and collectors
 * with no published map point, so "list a card with a photo" is the wrong
 * instruction for them. `action: "open_privacy"` lets the map open its own
 * visibility dialog instead of navigating.
 */
export const MATCHABLE_BLOCKED_COPY = {
  hidden: {
    title: "Make a card findable",
    body: "Your map visibility is set to Hidden, so nearby collectors can't find the cards you've listed. Fuzzy shows only a wider area, or Locked pins you to a spot you choose.",
    cta: "Change map visibility",
    href: "/map",
    action: "open_privacy",
  },
  no_location: {
    title: "Make a card findable",
    body: "Nearby collectors can't find your listed cards until the map knows roughly where you are. Open the map and allow location — only an approximate area is shown.",
    cta: "Open map",
    href: "/map",
  },
};

/** Copy for one mission step, honest about WHY `matchable` is not done. */
export const stepCopyFor = (mission, stepId) => {
  if (stepId === "matchable") {
    const blocked = MATCHABLE_BLOCKED_COPY[mission?.notFindableBecause];
    if (blocked) return blocked;
  }
  return STEP_COPY[stepId];
};

/** Discovery request outcomes the map can report. */
export const DISCOVERY_OK = "ok";
export const DISCOVERY_RATE_LIMITED = "rate_limited";
export const DISCOVERY_FAILED = "failed";
/**
 * The server refused, on purpose, because of where the user is (#102).
 *
 * Distinct from DISCOVERY_FAILED because nothing broke: `discoveryPolicy`
 * answers every discovery call outside the beta launch area with a deliberate
 * refusal. Bucketing it with a failure produced two lies at once — it blamed
 * the connection for a policy, and it offered a retry that replays the same
 * coordinates into the same bounds test and is therefore defined to fail.
 */
export const DISCOVERY_OUT_OF_AREA = "out_of_area";

/**
 * Why the map is empty, in the user's terms.
 *
 * An empty map used to say "No collectors nearby yet" regardless of cause — so
 * a user who had simply never built a wishlist was told the area was empty,
 * which is both wrong and unactionable.
 *
 * `discoveryOutcome` (#82): a failed or throttled discovery request must never
 * be rendered as a confirmed-empty neighbourhood. "No collectors nearby yet"
 * is reserved for a request that succeeded and genuinely returned nobody.
 * `not_matchable` still wins over a failed request — it is account state, true
 * whether or not the request went through, and its CTA is what the user should
 * act on either way.
 *
 * `out_of_area` (#102) is the one outcome that outranks `not_matchable`, and it
 * is the exception that proves that rule rather than a break with it. The
 * account-state CTA earns its precedence by being ACTIONABLE — a user who
 * builds a wishlist gets matches once the request goes through. Outside the
 * launch area no request will go through, so "Open collection" becomes an
 * instruction that cannot help, printed over the one fact that explains the
 * screen. A failed request is still ranked below account state, unchanged.
 */
export const emptyMapReason = (mission, discoveryOutcome = DISCOVERY_OK) => {
  if (discoveryOutcome === DISCOVERY_OUT_OF_AREA) {
    // Mirrors the server's own vocabulary — it says "the beta launch area" and
    // deliberately names no country and no bound, and neither does this. No
    // retry and no CTA: there is nothing here the user can do from where they
    // are standing, and offering a button that cannot work is worse than
    // offering none.
    return {
      reason: "out_of_area",
      headline: "Tradeloft isn't open here yet",
      body: "The beta covers a limited area, and where you are isn't part of it yet. Nothing's wrong with your account — we just aren't looking for collectors here.",
      cta: null,
      href: null,
      retry: null,
      // Only worth offering somewhere else to look if looking there would
      // actually work. With roaming off every discovery query outside the
      // launch area is refused, so a hotspot would be a button that reproduces
      // this very screen at a different coordinate.
      showHotspots: isRoamingEnabled(),
    };
  }
  if (mission && !mission.canMatch) {
    const step = mission.steps?.find((s) => s.id === "build_wishlist" && !s.done)
      ? "build_wishlist"
      : "add_cards";
    return {
      reason: "not_matchable",
      headline: "Nothing to match on yet",
      body: STEP_COPY[step].body,
      cta: STEP_COPY[step].cta,
      href: STEP_COPY[step].href,
    };
  }
  if (discoveryOutcome === DISCOVERY_RATE_LIMITED) {
    return {
      reason: "rate_limited",
      headline: "Search is taking a short break",
      body: "You've searched a lot in the last few minutes, so we paused for a moment. This says nothing about who's nearby — try again shortly.",
      cta: null,
      href: null,
      retry: "Try again",
    };
  }
  if (discoveryOutcome === DISCOVERY_FAILED) {
    return {
      reason: "discovery_failed",
      headline: "We couldn't check for collectors",
      body: "Something went wrong on our end or with your connection, so we don't know who's nearby right now.",
      cta: null,
      href: null,
      retry: "Try again",
    };
  }
  // Used to `return null`, which rendered NOTHING — no card, no message, no
  // retry — on a bare empty map. That is the worst of the states here: it is
  // indistinguishable from a broken app, and it fires whenever the mission
  // fetch fails, which is exactly when the user is least able to guess why.
  // The mission only supplies the CTA wording, so its absence is not a reason
  // to say nothing.
  if (!mission) {
    return {
      reason: "no_collectors_unknown_mission",
      headline: "No collectors nearby yet",
      body: "Nobody's in range right now. We couldn't load your next step either — that part usually fixes itself on a retry.",
      cta: null,
      href: null,
      retry: "Try again",
      showHotspots: isRoamingEnabled(),
    };
  }
  // "You're set up" is only true once OTHER collectors can find this account's
  // cards (P0 area B). A user with a wishlist but nothing listed-with-a-photo
  // can see matches but cannot be found — say that, with the one action that
  // fixes it, instead of telling them they are set up.
  if (mission && mission.findable === false) {
    // Why they cannot be found decides what to ask for: a Hidden collector
    // who already listed a photographed card must not be told to list one.
    const blocked = MATCHABLE_BLOCKED_COPY[mission.notFindableBecause];
    return {
      reason: "no_collectors_not_findable",
      headline: "No matches nearby yet",
      body: blocked
        ? `Nobody in range has your wishlist cards right now. ${blocked.body}`
        : "Nobody in range has your wishlist cards right now. List a card for trade with a photo so nearby collectors can find you too.",
      cta: blocked ? blocked.cta : STEP_COPY.matchable.cta,
      href: blocked ? blocked.href : STEP_COPY.matchable.href,
      action: blocked?.action || null,
      showHotspots: isRoamingEnabled(),
    };
  }
  return {
    reason: "no_collectors",
    headline: "No collectors nearby yet",
    // No longer says "widen your search". It said that for months with no
    // control anywhere in the app that widens anything — the hotspot list
    // below IS that control, so the copy now points at something real, and
    // says nothing about widening when the list is not being shown.
    body: isRoamingEnabled()
      ? "You're set up — there's just nobody in range right now. Here's where collectors actually are:"
      : "You're set up — there's just nobody in range right now. Check back later as more collectors join.",
    cta: null,
    href: null,
    // No retry, deliberately, and pinned by a test: the discovery request
    // SUCCEEDED and returned nobody. "Try again" is reserved here for a
    // request that actually failed, and offering it on a truthful empty answer
    // implies a fault that did not occur. The hotspot list is the action on
    // this branch.
    showHotspots: isRoamingEnabled(),
  };
};
